/**
 * feat-300 — the page-level owner of every AI edit thread on `/quiz-review`
 * (ADR §10.2, design NOTES §1).
 *
 * - ONE list call on load (`GET /api/tenant/{id}/question-edit-threads`).
 * - Polling with `scope=open` every {@link POLL_MS} **only while some thread
 *   `isWorking`**, and not at all otherwise (AT-18). The server's effective state
 *   is authoritative: the client never derives a timeout itself (ADR §3.3).
 * - A map from question id to its threads, the open-panel state per question,
 *   and the mutations. Cards read all of it through context, so
 *   `QuestionCardProps` never changes.
 * - `onApplied(question)` lets the page patch its content after an Apply.
 *
 * Without this provider mounted, `useQuestionEdit` returns `null` and every
 * voice-edit component renders nothing — which is what keeps a bare
 * `<QuestionCard>` byte-identical to feat-298's.
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  applyQuestionEditThread,
  discardQuestionEditThread,
  getQuestionEditThread,
  InputMode,
  isOpenThread,
  isWorking,
  listQuestionEditThreads,
  needsYou,
  openQuestionEditThread,
  QuestionEditApplyResult,
  QuestionEditThread,
  QuestionEditThreadList,
  replyQuestionEditThread,
  retryQuestionEditThread,
} from '../../../../services/questionEditService';
import { QuizQuestion } from '../../../../services/quizContentAdminService';
import { QUESTION_EDIT_COPY as COPY } from './voiceEditCopy';
import { useEditCoordinator } from '../editCoordinator';

/** ADR §10.2: 4 s (NOTES: "≤ 5 s"). */
export const POLL_MS = 4000;

/** One rendered English-column question, in document order. */
export interface QuestionPlacement {
  questionId: string;
  /** The `Q{n}` number the card shows. */
  index: number;
  /** feat-298's Drill key (`u{lesson}-l{drill}`). */
  drillKey: string;
  /** The item's `data-item-key` (`{drillKey}-q{id}`). */
  itemKey: string;
  /** The Lesson (`QuizUnit`) holding it — whose pending set an Apply re-reads. */
  lessonId?: string;
}

export type PanelIntent = 'voice' | 'type' | 'show';

export interface QuestionEditContextValue {
  /** The list call has answered at least once. */
  loaded: boolean;
  /** The last list call failed (and none has succeeded since). */
  loadFailed: boolean;
  reload: () => void;
  /** `ThreadList.approval_required`; `null` until known. */
  approvalRequired: boolean | null;
  currentEmail: string | null;
  threadsFor: (questionId: string) => QuestionEditThread[];
  openThreadFor: (questionId: string) => QuestionEditThread | null;
  /** The thread the panel shows: the open one, else one closed in this session
   *  until the reviewer hides the panel (NOTES §3.7). */
  panelThreadFor: (questionId: string) => QuestionEditThread | null;
  isPanelOpen: (questionId: string) => boolean;
  setPanelOpen: (questionId: string, open: boolean) => void;
  /** What opening the panel was FOR, consumed once by the panel on mount/open:
   *  `voice` starts recording, `type` focuses the box, `show` does neither. */
  setIntent: (questionId: string, intent: PanelIntent) => void;
  takeIntent: (questionId: string) => PanelIntent | null;
  /** Keep a closed thread on its open panel until Hide (e.g. after a 409 that
   *  says someone else closed it). */
  stickThread: (questionId: string, threadId: string) => void;
  /** Ask a panel to focus its heading once it is mounted (counter jump, Send). */
  focusRequest: string | null;
  requestFocus: (questionId: string | null) => void;
  placementOf: (questionId: string) => QuestionPlacement | null;
  /** Needs-you question ids, in document order (rendered questions only). */
  needsYouIds: string[];
  start: (questionId: string, text: string, mode: InputMode) => Promise<QuestionEditThread>;
  reply: (threadId: string, text: string, mode: InputMode) => Promise<QuestionEditThread>;
  retry: (threadId: string) => Promise<QuestionEditThread>;
  apply: (threadId: string) => Promise<QuestionEditApplyResult>;
  discard: (threadId: string) => Promise<QuestionEditThread>;
  fetchThread: (threadId: string) => Promise<QuestionEditThread>;
  /** Merge a thread the server handed back (e.g. on a 409 envelope). */
  mergeThread: (t: QuestionEditThread) => void;
  /** Re-read the list (e.g. after a `thread_open` 409). */
  refresh: () => Promise<void>;
  announce: (message: string) => void;
}

const QuestionEditContext = React.createContext<QuestionEditContextValue | null>(null);

/** `null` outside a provider — callers render nothing then. */
export function useQuestionEdit(): QuestionEditContextValue | null {
  return useContext(QuestionEditContext);
}

/** The header counter's inputs. `null` outside a provider. */
export function useNeedsYou(): { ids: string[]; ctx: QuestionEditContextValue } | null {
  const ctx = useContext(QuestionEditContext);
  return ctx ? { ids: ctx.needsYouIds, ctx } : null;
}

type ThreadMap = ReadonlyMap<string, QuestionEditThread>;

function mergeInto(prev: ThreadMap, threads: QuestionEditThread[]): Map<string, QuestionEditThread> {
  const next = new Map(prev);
  for (const t of threads) {
    const old = next.get(t.id);
    // A closed thread arrives WITHOUT messages in the list; keep any we already have.
    next.set(t.id, old && !t.messages && old.messages ? { ...t, messages: old.messages } : t);
  }
  return next;
}

export const QuestionEditProvider: React.FC<{
  tenantId: string | null | undefined;
  placements: QuestionPlacement[];
  onApplied?: (question: QuizQuestion) => void;
  /** The page's hook for feat-299's inline question saves (R2 3c): the provider
   *  installs its handler here while mounted. */
  questionSavedRef?: React.MutableRefObject<((questionId: string) => void) | null>;
  currentEmail?: string | null;
  children: React.ReactNode;
}> = ({ tenantId, placements, onApplied, questionSavedRef, currentEmail = null, children }) => {
  const [threads, setThreads] = useState<ThreadMap>(() => new Map());
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [approvalRequired, setApprovalRequired] = useState<boolean | null>(null);
  const [openPanels, setOpenPanels] = useState<ReadonlySet<string>>(() => new Set());
  const [sticky, setSticky] = useState<ReadonlyMap<string, string>>(() => new Map());
  const [focusRequest, setFocusRequest] = useState<string | null>(null);
  const intentsRef = useRef(new Map<string, PanelIntent>());
  const setIntent = useCallback((qid: string, intent: PanelIntent) => {
    intentsRef.current.set(qid, intent);
  }, []);
  const takeIntent = useCallback((qid: string) => {
    const i = intentsRef.current.get(qid) ?? null;
    intentsRef.current.delete(qid);
    return i;
  }, []);
  const stickThread = useCallback((qid: string, threadId: string) => {
    setSticky((prev) => (prev.get(qid) === threadId ? prev : new Map(prev).set(qid, threadId)));
  }, []);
  const [liveMessage, setLiveMessage] = useState('');
  const loggedFailureRef = useRef(false);
  const tenantRef = useRef(tenantId);
  tenantRef.current = tenantId;
  const threadsRef = useRef(threads);
  threadsRef.current = threads;
  const onAppliedRef = useRef(onApplied);
  onAppliedRef.current = onApplied;

  const placementMap = useMemo(() => {
    const m = new Map<string, QuestionPlacement>();
    placements.forEach((p) => {
      if (!m.has(p.questionId)) m.set(p.questionId, p);
    });
    return m;
  }, [placements]);
  const placementRef = useRef(placementMap);
  placementRef.current = placementMap;

  const announce = useCallback((message: string) => {
    // Clear first so the same sentence twice is still announced.
    setLiveMessage('');
    window.setTimeout(() => setLiveMessage(message), 30);
  }, []);

  const announceRef = useRef(announce);
  announceRef.current = announce;

  const applyList = useCallback((list: QuestionEditThreadList, full: boolean) => {
    setApprovalRequired(typeof list.approval_required === 'boolean' ? list.approval_required : null);
    // AI turns that arrived since the last read → one polite announcement each.
    // Computed from the ref, OUTSIDE the state updater, so StrictMode's double
    // invocation of updaters cannot announce twice.
    const prev = threadsRef.current;
    for (const t of list.threads) {
      const old = prev.get(t.id);
      if (old && isWorking(old) && !isWorking(t) && isOpenThread(t)) {
        const p = placementRef.current.get(t.question_item_id);
        if (p) announceRef.current(COPY.liveReplied(p.index));
      }
    }
    setThreads((current) => {
      if (!full) return mergeInto(current, list.threads);
      // The full list is authoritative for every thread it names.
      const keep = new Map<string, QuestionEditThread>();
      list.threads.forEach((t) => {
        const old = current.get(t.id);
        keep.set(t.id, old && !t.messages && old.messages ? { ...t, messages: old.messages } : t);
      });
      return keep;
    });
  }, []);

  const refresh = useCallback(async () => {
    const tenant = tenantRef.current;
    if (!tenant) return;
    try {
      const list = await listQuestionEditThreads(tenant, 'all');
      if (tenantRef.current !== tenant) return;
      applyList(list, true);
      setLoaded(true);
      setLoadFailed(false);
      loggedFailureRef.current = false;
    } catch (e) {
      if (tenantRef.current !== tenant) return;
      setLoadFailed(true);
      if (!loggedFailureRef.current) {
        loggedFailureRef.current = true;
        // Status only — `fetchApi`'s own log already carries no body (feat-243).
        // eslint-disable-next-line no-console
        console.error('[QuestionEdit] GET question-edit-threads failed');
      }
    }
  }, [applyList]);

  // R2 3c — an inline save changed a question under an open proposal. `is_stale`
  // is the server's fingerprint compare (ADR §4.3), so re-read the list (one GET)
  // rather than guess: a save that wrote the same value is NOT stale. Only when
  // that question has a `proposal_ready` thread; the 409 still covers the race.
  useEffect(() => {
    if (!questionSavedRef) return undefined;
    const handler = (qid: string) => {
      let proposalOpen = false;
      threadsRef.current.forEach((t) => {
        if (t.question_item_id === qid && t.state === 'proposal_ready') proposalOpen = true;
      });
      if (proposalOpen) refresh();
    };
    questionSavedRef.current = handler;
    return () => {
      if (questionSavedRef.current === handler) questionSavedRef.current = null;
    };
  }, [questionSavedRef, refresh]);

  // Load on mount and on tenant change.
  useEffect(() => {
    setThreads(new Map());
    setLoaded(false);
    setLoadFailed(false);
    setApprovalRequired(null);
    setOpenPanels(new Set());
    setSticky(new Map());
    if (tenantId) refresh();
  }, [tenantId, refresh]);

  // --- polling (AT-18) -------------------------------------------------------
  const anyWorking = useMemo(() => {
    let w = false;
    threads.forEach((t) => {
      if (isWorking(t)) w = true;
    });
    return w;
  }, [threads]);

  useEffect(() => {
    if (!anyWorking || !tenantId) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const list = await listQuestionEditThreads(tenantId, 'open');
        if (cancelled || tenantRef.current !== tenantId) return;
        // A previously-open thread missing from `scope=open` was closed elsewhere:
        // re-read everything so it lands in history with its real state.
        const openNow = new Set(list.threads.map((t) => t.id));
        let closedElsewhere = false;
        threadsRef.current.forEach((t) => {
          if (isOpenThread(t) && !openNow.has(t.id)) closedElsewhere = true;
        });
        if (closedElsewhere) {
          await refresh();
        } else {
          applyList(list, false);
        }
      } catch {
        // Keep polling on the next tick: a transient failure must not freeze a
        // `working` chip. The tick re-arms because `threads` is unchanged → we
        // nudge it with a no-op state copy.
        if (!cancelled) setThreads((prev) => new Map(prev));
      }
    }, POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [anyWorking, tenantId, threads, applyList, refresh]);

  // --- reads ------------------------------------------------------------------
  const byQuestion = useMemo(() => {
    const m = new Map<string, QuestionEditThread[]>();
    threads.forEach((t) => {
      const list = m.get(t.question_item_id) ?? [];
      list.push(t);
      m.set(t.question_item_id, list);
    });
    // Newest first.
    m.forEach((list) => list.sort((a, b) => (a.created_at < b.created_at ? 1 : -1)));
    return m;
  }, [threads]);

  const threadsFor = useCallback((qid: string) => byQuestion.get(qid) ?? [], [byQuestion]);
  const openThreadFor = useCallback(
    (qid: string) => (byQuestion.get(qid) ?? []).find(isOpenThread) ?? null,
    [byQuestion],
  );
  const panelThreadFor = useCallback(
    (qid: string) => {
      const open = (byQuestion.get(qid) ?? []).find(isOpenThread);
      if (open) return open;
      const id = sticky.get(qid);
      return id ? threads.get(id) ?? null : null;
    },
    [byQuestion, sticky, threads],
  );

  const needsYouIds = useMemo(() => {
    const ids: string[] = [];
    const seen = new Set<string>();
    for (const p of placements) {
      if (seen.has(p.questionId)) continue;
      seen.add(p.questionId);
      const open = (byQuestion.get(p.questionId) ?? []).find(isOpenThread);
      if (open && needsYou(open)) ids.push(p.questionId);
    }
    return ids;
  }, [placements, byQuestion]);

  // --- panel state -------------------------------------------------------------
  const isPanelOpen = useCallback((qid: string) => openPanels.has(qid), [openPanels]);
  // R2 3d: opening a panel commits any open feat-299 inline editor through its
  // coordinator (the same rule as opening a second field). `null` outside the page.
  const coordinator = useEditCoordinator();
  const coordinatorRef = useRef(coordinator);
  coordinatorRef.current = coordinator;
  const setPanelOpen = useCallback((qid: string, open: boolean) => {
    if (open) coordinatorRef.current?.commitOpen();
    setOpenPanels((prev) => {
      if (prev.has(qid) === open) return prev;
      const next = new Set(prev);
      if (open) next.add(qid);
      else next.delete(qid);
      return next;
    });
    if (!open) {
      // Hiding releases a just-closed thread to the history list.
      setSticky((prev) => {
        if (!prev.has(qid)) return prev;
        const next = new Map(prev);
        next.delete(qid);
        return next;
      });
    }
  }, []);

  // --- mutations ---------------------------------------------------------------
  const mergeThread = useCallback((t: QuestionEditThread) => {
    setThreads((prev) => mergeInto(prev, [t]));
  }, []);

  const start = useCallback(
    async (qid: string, text: string, mode: InputMode) => {
      const t = await openQuestionEditThread(qid, text, mode);
      mergeThread(t);
      return t;
    },
    [mergeThread],
  );
  const reply = useCallback(
    async (threadId: string, text: string, mode: InputMode) => {
      const t = await replyQuestionEditThread(threadId, text, mode);
      mergeThread(t);
      return t;
    },
    [mergeThread],
  );
  const retry = useCallback(
    async (threadId: string) => {
      const t = await retryQuestionEditThread(threadId);
      mergeThread(t);
      return t;
    },
    [mergeThread],
  );
  const apply = useCallback(
    async (threadId: string) => {
      const result = await applyQuestionEditThread(threadId);
      const old = threadsRef.current.get(threadId);
      mergeThread(old?.messages && !result.thread.messages ? { ...result.thread, messages: old.messages } : result.thread);
      setSticky((prev) => new Map(prev).set(result.thread.question_item_id, threadId));
      setApprovalRequired(result.approval_required);
      // The page patches the tree AND re-reads this question's Lesson pending set
      // through feat-299's save tail (ADR §10.6) — the chip comes from that read.
      onAppliedRef.current?.(result.question);
      return result;
    },
    [mergeThread],
  );
  const discard = useCallback(
    async (threadId: string) => {
      const t = await discardQuestionEditThread(threadId);
      const old = threadsRef.current.get(threadId);
      mergeThread(old?.messages && !t.messages ? { ...t, messages: old.messages } : t);
      setSticky((prev) => new Map(prev).set(t.question_item_id, threadId));
      return t;
    },
    [mergeThread],
  );
  const fetchThread = useCallback(
    async (threadId: string) => {
      const t = await getQuestionEditThread(threadId);
      mergeThread(t);
      return t;
    },
    [mergeThread],
  );

  const value = useMemo<QuestionEditContextValue>(
    () => ({
      loaded,
      loadFailed,
      reload: () => {
        refresh();
      },
      approvalRequired,
      currentEmail,
      threadsFor,
      openThreadFor,
      panelThreadFor,
      isPanelOpen,
      setPanelOpen,
      setIntent,
      takeIntent,
      stickThread,
      focusRequest,
      requestFocus: setFocusRequest,
      placementOf: (qid) => placementMap.get(qid) ?? null,
      needsYouIds,
      start,
      reply,
      retry,
      apply,
      discard,
      fetchThread,
      mergeThread,
      refresh,
      announce,
    }),
    [
      loaded,
      loadFailed,
      refresh,
      approvalRequired,
      currentEmail,
      threadsFor,
      openThreadFor,
      panelThreadFor,
      isPanelOpen,
      setPanelOpen,
      setIntent,
      takeIntent,
      stickThread,
      focusRequest,
      placementMap,
      needsYouIds,
      start,
      reply,
      retry,
      apply,
      discard,
      fetchThread,
      mergeThread,
      announce,
    ],
  );

  return (
    <QuestionEditContext.Provider value={value}>
      {children}
      {/* ONE polite region per page, not per panel (NOTES §5). */}
      <div className="sr-only" role="status" aria-live="polite" data-testid="qe-live">
        {liveMessage}
      </div>
    </QuestionEditContext.Provider>
  );
};
